// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Types and API
import { addProgressiveLens } from "../../../api";
import { AddEditProgressiveLensType } from "src/services/clinics/types";

export const useAddProgressiveLens = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (lensData: AddEditProgressiveLensType) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return addProgressiveLens(token, clinicId, lensData);
      } catch (error) {
        throw error;
      }
    },
    // Invalidate clinic lens query cache
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["progressive-lenses", clinicId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("Lens added successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Lens could not be added!"),
      });
    },
  });
};
